import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import './Earnings.css';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

interface EmployeeStat {
    employeeId: string;
    name: string;
    totalRevenue: number;
    totalTips: number;
    totalAmount: number;
    completedServicesCount: number;
}

interface TimelineStat {
    date: string;
    revenue: number;
    tips: number;
    total: number;
    count: number;
}

interface EarningsData {
    totalOverall: number;
    totalOverallRevenue: number;
    totalOverallTips: number;
    stats: EmployeeStat[];
    timeline?: TimelineStat[];
}

const Earnings: React.FC = () => {
    const { user } = useAuth() as any;
    const { t } = useLanguage() as any;
    const [earningsData, setEarningsData] = useState<EarningsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(new Date().toLocaleDateString('en-CA'));
    const [endDate, setEndDate] = useState(new Date().toLocaleDateString('en-CA'));
    const [quickRange, setQuickRange] = useState('today');

    const businessId = localStorage.getItem('currentBusinessId') || '';
    const userRole: string = (user?.role || 'staff').toLowerCase();
    const isManagerOrOwner = ['owner', 'admin', 'manager'].includes(userRole);

    const fetchEarnings = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        try {
            const data = await apiService.getEarningsStats(businessId, startDate, endDate);
            setEarningsData(data);
        } catch (err: any) {
            console.error('Failed to fetch earnings', err);
        } finally {
            setLoading(false);
        }
    }, [businessId, startDate, endDate]);

    useEffect(() => {
        fetchEarnings();
    }, [fetchEarnings]);

    const handleQuickRange = (range: string) => {
        setQuickRange(range);
        const today = new Date().toLocaleDateString('en-CA');
        if (range === 'today') {
            setStartDate(today);
            setEndDate(today);
        } else if (range === 'week') {
            const d = new Date();
            d.setDate(d.getDate() - 6);
            setStartDate(d.toLocaleDateString('en-CA'));
            setEndDate(today);
        } else if (range === 'month') {
            const d = new Date();
            d.setDate(1);
            setStartDate(d.toLocaleDateString('en-CA'));
            setEndDate(today);
        }
    };

    // ---- Personal stats (first record for staff, total for owner) ----
    const personalStat: EmployeeStat | null = isManagerOrOwner
        ? null  // owners see full table; personal card is business total
        : (earningsData?.stats?.[0] || null);

    const exportToCSV = () => {
        if (!earningsData?.stats?.length) return;
        const headers = [
            t('earnings.table.employee'), 
            t('earnings.table.servicesCount'), 
            `${t('earnings.table.serviceRevenue')} ($)`, 
            `${t('earnings.table.tip')} ($)`, 
            `${t('earnings.table.total')} ($)`
        ];
        const rows = earningsData.stats.map(s => [
            s.name,
            s.completedServicesCount,
            s.totalRevenue.toFixed(2),
            s.totalTips.toFixed(2),
            s.totalAmount.toFixed(2)
        ]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `earnings-${startDate}_${endDate}.csv`;
        a.click();
    };

    if (loading) {
        return (
            <div className="earnings-container earnings-loading">
                <div className="loading-spinner" style={{ width: 44, height: 44 }} />
            </div>
        );
    }

    return (
        <div className="earnings-container">
            {/* ── Header ── */}
            <div className="earnings-header">
                <div>
                    <h1>💰 {t('earnings.title')}</h1>
                    <p className="earnings-subtitle">{t('earnings.subtitle')}</p>
                </div>
                {isManagerOrOwner && (
                    <button className="export-btn" onClick={exportToCSV}>
                        ⬇️ {t('earnings.exportCSV')}
                    </button>
                )}
            </div>

            {/* ── Quick Range Filters ── */}
            <div className="earnings-filters">
                <div className="filter-group">
                    {[
                        { key: 'today', label: t('earnings.today') },
                        { key: 'week',  label: t('earnings.week') },
                        { key: 'month', label: t('earnings.month') },
                        { key: 'custom', label: t('earnings.custom') }
                    ].map(({ key, label }) => (
                        <button
                            key={key}
                            className={`filter-btn${quickRange === key ? ' active' : ''}`}
                            onClick={() => key === 'custom' ? setQuickRange('custom') : handleQuickRange(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {quickRange === 'custom' && (
                    <div className="custom-date-range">
                        <label>{t('earnings.from')}: <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>
                        <span>—</span>
                        <label>{t('earnings.to')}: <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} /></label>
                    </div>
                )}
            </div>

            {/* ── Summary Cards ── */}
            <div className="earnings-summary">
                {isManagerOrOwner ? (
                    // Owner/Manager: show business totals
                    <>
                        <div className="stat-card highlight">
                            <div className="stat-icon">💵</div>
                            <div>
                                <p className="stat-label">{t('earnings.totalBusinessRevenue')}</p>
                                <p className="stat-value">${earningsData?.totalOverall?.toFixed(2) ?? '0.00'}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">✂️</div>
                            <div>
                                <p className="stat-label">{t('earnings.serviceRevenue')}</p>
                                <p className="stat-value">${earningsData?.totalOverallRevenue?.toFixed(2) ?? '0.00'}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">💝</div>
                            <div>
                                <p className="stat-label">{t('earnings.totalTip')}</p>
                                <p className="stat-value">${earningsData?.totalOverallTips?.toFixed(2) ?? '0.00'}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">👥</div>
                            <div>
                                <p className="stat-label">{t('earnings.employeesWithRevenue')}</p>
                                <p className="stat-value">{earningsData?.stats?.length ?? 0}</p>
                            </div>
                        </div>
                    </>
                ) : (
                    // Staff: show their personal stats
                    <>
                        <div className="stat-card highlight">
                            <div className="stat-icon">💵</div>
                            <div>
                                <p className="stat-label">{t('earnings.myTotalRevenue')}</p>
                                <p className="stat-value">${personalStat?.totalAmount?.toFixed(2) ?? '0.00'}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">✂️</div>
                            <div>
                                <p className="stat-label">{t('earnings.serviceRevenue')}</p>
                                <p className="stat-value">${personalStat?.totalRevenue?.toFixed(2) ?? '0.00'}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">💝</div>
                            <div>
                                <p className="stat-label">{t('earnings.tip')}</p>
                                <p className="stat-value">${personalStat?.totalTips?.toFixed(2) ?? '0.00'}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">🧾</div>
                            <div>
                                <p className="stat-label">{t('earnings.completedServices')}</p>
                                <p className="stat-value">{personalStat?.completedServicesCount ?? 0}</p>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ── Owner/Manager: Chart ── */}
            {isManagerOrOwner && earningsData?.timeline && earningsData.timeline.length > 0 && (
                <div className="earnings-chart-wrapper" style={{ background: '#fff', borderRadius: '14px', padding: '24px', marginBottom: '28px', border: '1px solid #edf2f7', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#2d3748', margin: '0 0 20px' }}>📈 {t('earnings.chartTitle') || 'Revenue Trend'}</h2>
                    <ResponsiveContainer width="100%" height={350}>
                        <LineChart data={earningsData.timeline} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 12, fill: '#718096' }}
                                tickFormatter={(val) => {
                                    const d = new Date(val + 'T12:00:00');
                                    return `${d.getDate()}/${d.getMonth()+1}`;
                                }}
                                axisLine={{ stroke: '#e2e8f0' }}
                                tickLine={false}
                            />
                            <YAxis 
                                tick={{ fontSize: 12, fill: '#718096' }}
                                tickFormatter={(val) => `$${val}`}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip 
                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}
                                labelFormatter={(val) => `Ngày: ${val}`}
                                formatter={(value: number, name: string) => [`$${(value as number).toFixed(2)}`, name === 'total' ? 'Tổng thu' : name === 'revenue' ? 'Doanh thu DV' : 'Tiền Tip']}
                            />
                            <Legend 
                                wrapperStyle={{ paddingTop: '16px', fontSize: '13px' }}
                                formatter={(name) => name === 'total' ? 'Tổng thu' : name === 'revenue' ? 'Doanh thu DV' : 'Tiền Tip'}
                            />
                            <Line type="monotone" dataKey="total" name="total" stroke="#059669" strokeWidth={3} dot={{ r: 5, fill: '#059669', strokeWidth: 0 }} activeDot={{ r: 7 }} />
                            <Line type="monotone" dataKey="revenue" name="revenue" stroke="#4f46e5" strokeWidth={2} dot={{ r: 4, fill: '#4f46e5', strokeWidth: 0 }} />
                            <Line type="monotone" dataKey="tips" name="tips" stroke="#d97706" strokeWidth={2} dot={{ r: 4, fill: '#d97706', strokeWidth: 0 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── Owner/Manager: Full Employee Table ── */}
            {isManagerOrOwner && (
                <div className="earnings-table-wrapper">
                    <div className="table-header-row">
                        <h2>📊 {t('earnings.fullStatsTitle')}</h2>
                        <span className="table-date-range">{startDate === endDate ? startDate : `${startDate} → ${endDate}`}</span>
                    </div>

                    {earningsData?.stats && earningsData.stats.length > 0 ? (
                        <div className="table-scroll">
                            <table className="earnings-table">
                                <thead>
                                    <tr>
                                        <th>{t('earnings.table.index')}</th>
                                        <th>{t('earnings.table.employee')}</th>
                                        <th>{t('earnings.table.servicesCount')}</th>
                                        <th>{t('earnings.table.serviceRevenue')}</th>
                                        <th>{t('earnings.table.tip')}</th>
                                        <th>{t('earnings.table.total')}</th>
                                        <th>{t('earnings.table.percentTotal')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {earningsData.stats
                                        .sort((a, b) => b.totalAmount - a.totalAmount)
                                        .map((stat, idx) => {
                                            const pct = earningsData.totalOverall > 0
                                                ? (stat.totalAmount / earningsData.totalOverall * 100).toFixed(1)
                                                : '0.0';
                                            return (
                                                <tr key={stat.employeeId}>
                                                    <td className="rank">{idx + 1}</td>
                                                    <td>
                                                        <div className="emp-name-badge">
                                                            <div className="avatar" style={{
                                                                background: `hsl(${(stat.name.charCodeAt(0) * 37) % 360}, 60%, 85%)`,
                                                                color: `hsl(${(stat.name.charCodeAt(0) * 37) % 360}, 60%, 35%)`
                                                            }}>
                                                                {stat.name.charAt(0).toUpperCase()}
                                                            </div>
                                                            {stat.name}
                                                        </div>
                                                    </td>
                                                    <td className="center">{stat.completedServicesCount}</td>
                                                    <td className="amount">${stat.totalRevenue.toFixed(2)}</td>
                                                    <td className="amount tip">${stat.totalTips.toFixed(2)}</td>
                                                    <td className="amount total">${stat.totalAmount.toFixed(2)}</td>
                                                    <td>
                                                        <div className="pct-bar-wrap">
                                                            <div className="pct-bar" style={{ width: `${pct}%` }} />
                                                            <span>{pct}%</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    }
                                </tbody>
                                <tfoot>
                                    <tr className="total-row">
                                        <td colSpan={2}>{t('earnings.table.grandTotal')}</td>
                                        <td className="center">
                                            {earningsData.stats.reduce((s, e) => s + e.completedServicesCount, 0)}
                                        </td>
                                        <td className="amount">${earningsData.totalOverallRevenue.toFixed(2)}</td>
                                        <td className="amount tip">${earningsData.totalOverallTips.toFixed(2)}</td>
                                        <td className="amount total">${earningsData.totalOverall.toFixed(2)}</td>
                                        <td>100%</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (
                        <div className="no-data">
                            <span>📭</span>
                            <p>{t('earnings.noData')}</p>
                            <small>{t('earnings.noDataHint1')}</small>
                        </div>
                    )}
                </div>
            )}

            {/* ── Staff: Personal history notice ── */}
            {!isManagerOrOwner && !personalStat && (
                <div className="earnings-table-wrapper">
                    <div className="no-data">
                        <span>📭</span>
                        <p>{t('earnings.noStaffData')}</p>
                        <small>{t('earnings.noStaffDataHint')}</small>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Earnings;
